/**
 * People step of the cycle configure wizard.
 *
 * Who takes part is each track's roster (`track_participants`); everyone on the
 * cycle's own roster (`cycle_participants`) is on every track already. This step
 * adds people to, or takes them off, the rosters of the tracks it lists under
 * "Writes to", through the calls the track page's Participation tab makes
 * (`useTrackParticipantsStore.addParticipant` / `removeParticipant`). A track whose
 * people are locked is listed as locked and left alone (the server refuses it,
 * migration 1031). It does not write the cycle's roster: someone added there would
 * join every track, the locked ones included.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Users } from 'lucide-react';
import { useTrackParticipantsStore } from '../../../stores/prioritization/trackParticipantsStore';
import { useCycleParticipantsStore } from '../../../stores/prioritization/cycleParticipantsStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { keyCoverage, planTrackWrites, wizardTrackTarget } from '../../../lib/cycleWizardTargets';
import {
  CycleTrackTargets,
  TrackWriteReport,
  type TrackWriteFailure,
} from './CycleTrackTargets';
import { useChosenTracks, useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';

export interface CyclePeoplePanelProps {
  cycleId: string;
  userId: string;
}

export function CyclePeoplePanel({ cycleId, userId }: CyclePeoplePanelProps) {
  const { tracks } = useCycleOpenTracks(cycleId);
  const targets = tracks.map(t => wizardTrackTarget('people', t));
  const { chosenIds, toggle: toggleTrack } = useChosenTracks(targets);
  const mayWrite = chosenIds.length > 0;

  const trackParticipants = useTrackParticipantsStore(s => s.participants);
  const fetchTrackParticipants = useTrackParticipantsStore(s => s.fetchParticipantsForTrack);
  const addTrackParticipant = useTrackParticipantsStore(s => s.addParticipant);
  const removeTrackParticipant = useTrackParticipantsStore(s => s.removeParticipant);
  const cycleParticipants = useCycleParticipantsStore(s => s.participants);
  const fetchCycleParticipants = useCycleParticipantsStore(s => s.fetchParticipantsForCycle);

  const trackIdsKey = tracks.map(t => t.id).join(',');
  useEffect(() => {
    void fetchCycleParticipants(cycleId);
    for (const id of trackIdsKey ? trackIdsKey.split(',') : []) void fetchTrackParticipants(id);
  }, [cycleId, trackIdsKey, fetchCycleParticipants, fetchTrackParticipants]);

  // The workspace's people, as the track's Participation tab lists them.
  const currentWorkspaceId = useTenantStore(s => s.currentWorkspaceId);
  const workspaceId = currentWorkspaceId ?? getCurrentWorkspaceId();
  const { users, loading: loadingUsers } = useSystemUsers(workspaceId ? { workspaceId } : undefined);
  const people = useMemo(
    () => [...users].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [users],
  );

  const onCycleRoster = useMemo(
    () => new Set(cycleParticipants.filter(p => p.cycleId === cycleId && p.status === 'active').map(p => p.userId)),
    [cycleParticipants, cycleId],
  );

  // Who is on each track's own roster now.
  const present = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const t of tracks) {
      map.set(t.id, new Set(trackParticipants.filter(p => p.trackId === t.id && p.status === 'active').map(p => p.userId)));
    }
    return map;
  }, [tracks, trackParticipants]);

  const [decided, setDecided] = useState<Map<string, boolean>>(new Map());
  const isOn = (personId: string) => decided.get(personId) ?? keyCoverage(present, chosenIds, personId) === 'all';
  const toggle = (personId: string) => {
    if (!mayWrite) return;
    const next = new Map(decided);
    next.set(personId, !isOn(personId));
    setDecided(next);
  };

  const plan = planTrackWrites(present, chosenIds, decided);
  const changeCount = plan.add.length + plan.remove.length;

  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<{ saved: number; failures: TrackWriteFailure[] } | null>(null);
  const trackName = (id: string) => tracks.find(t => t.id === id)?.name ?? 'A track';
  const nameOf = (id: string) => {
    const u = users.find(x => x.id === id);
    return u?.name || u?.email || 'Someone';
  };

  const handleSave = async () => {
    setSaving(true);
    const failures: TrackWriteFailure[] = [];
    let saved = 0;
    const refused = () => useTrackParticipantsStore.getState().error ?? 'The server refused the change';
    try {
      for (const { trackId, key } of plan.add) {
        const added = await addTrackParticipant({ trackId, userId: key, role: 'contributor', addedBy: userId });
        if (added) saved += 1;
        else failures.push({ trackName: trackName(trackId), what: `adding ${nameOf(key)}`, error: refused() });
      }
      for (const { trackId, key } of plan.remove) {
        const row = trackParticipants.find(p => p.trackId === trackId && p.userId === key && p.status === 'active');
        if (!row) continue;
        if (await removeTrackParticipant(row.id, userId)) saved += 1;
        else failures.push({ trackName: trackName(trackId), what: `removing ${nameOf(key)}`, error: refused() });
      }
      await Promise.all(chosenIds.map(id => fetchTrackParticipants(id)));
      setDecided(new Map());
      setReport({ saved, failures });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)] flex items-center gap-2">
          <Users className="h-5 w-5 text-[var(--text-ter)]" />
          People
        </h3>
        <p className="text-sm text-[var(--text-sec)] mt-1">
          Tick who takes part. Saving adds them to each track below and takes unticked people
          off — what “Add to this track” and Remove do on a track&apos;s Participation tab.
        </p>
      </div>

      <CycleTrackTargets targets={targets} chosenIds={chosenIds} onToggle={toggleTrack} />

      {onCycleRoster.size > 0 && (
        <p className="text-sm text-[var(--text-sec)]">
          On the cycle&apos;s roster, so on every track already: {[...onCycleRoster].map(nameOf).join(', ')}.
        </p>
      )}

      {report && <TrackWriteReport saved={report.saved} failures={report.failures} />}

      {loadingUsers ? (
        <p className="text-sm text-[var(--text-ter)] flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading people…
        </p>
      ) : (
        <ul className="space-y-1">
          {people.map(person => {
            const on = isOn(person.id);
            const coverage = keyCoverage(present, chosenIds, person.id);
            const onCount = chosenIds.filter(id => present.get(id)?.has(person.id)).length;
            return (
              <li key={person.id} className="flex items-center justify-between gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  aria-label={person.name || person.email}
                  disabled={!mayWrite}
                  onClick={() => toggle(person.id)}
                  className="flex items-center gap-3 text-left"
                >
                  <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    on ? 'bg-[var(--slate)] border-[var(--slate)]' : 'border-[var(--rule)]'
                  }`}>
                    {on && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span>
                    <span className="block text-sm text-[var(--text-pri)]">{person.name || person.email}</span>
                    {person.name && <span className="block text-xs text-[var(--text-ter)]">{person.email}</span>}
                  </span>
                </button>
                {coverage === 'some' && (
                  <span className="text-xs text-[var(--text-ter)]">On {onCount} of {chosenIds.length} tracks</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {mayWrite && changeCount > 0 && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--rule)]">
          <span className="text-sm text-[var(--text-sec)]">
            {[plan.add.length > 0 ? `Adds ${plan.add.length}` : null, plan.remove.length > 0 ? `removes ${plan.remove.length}` : null]
              .filter(Boolean)
              .join(', ')}
          </span>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save people to {chosenIds.length} track{chosenIds.length === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
}

export default CyclePeoplePanel;
